import { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import logoSvg from "@/assets/logo.svg";

export interface UnifiedCardProps {
  /** Unique ID for data attribute */
  id: string;
  /** Card type identifier */
  cardType: "world" | "adventure" | "shared" | "public" | "trash";
  /** Click handler */
  onClick?: () => void;

  // --- Banner ---
  bannerUrl?: string | null;
  /** Apply grayscale to banner (trash) */
  grayscale?: boolean;

  // --- Icon ---
  icon?: string | null;
  /** Fallback emoji if no icon */
  iconFallback?: string;

  // --- Title ---
  title: string;

  // --- Description ---
  description?: string | null;

  // --- Badges shown top-right on banner or inline ---
  badges?: ReactNode;
  /** Badges shown top-left on banner */
  badgesLeft?: ReactNode;

  // --- Actions (3-dot menu) - shown on banner or title row ---
  actions?: ReactNode;
  /** Actions placed in footer row instead of title/banner */
  footerActions?: ReactNode;

  // --- Footer content (date, stats, etc.) ---
  footer?: ReactNode;

  // --- Bottom actions row (restore/delete for trash) ---
  bottomActions?: ReactNode;

  // --- Extra content between description and footer ---
  extraContent?: ReactNode;

  // --- Highlight ring ---
  highlighted?: boolean;
  /** Top accent bar */
  accentBar?: boolean;

  // --- Confidential indicator ---
  confidential?: boolean;

  // --- Additional class ---
  className?: string;
}

export function UnifiedCard({
  id,
  cardType,
  onClick,
  bannerUrl,
  grayscale,
  icon,
  iconFallback = "🌍",
  title,
  description,
  badges,
  badgesLeft,
  actions,
  footerActions,
  footer,
  bottomActions,
  extraContent,
  highlighted,
  accentBar,
  confidential,
  className,
}: UnifiedCardProps) {
  const isImageIcon = icon && (icon.startsWith("http") || icon.startsWith("data:"));
  const displayIcon = icon || iconFallback;

  return (
    <Card
      data-card={cardType}
      data-card-id={id}
      className={cn(
        "overflow-hidden transition-all group h-auto sm:h-[480px] flex flex-col",
        onClick && "cursor-pointer",
        highlighted
          ? "border-primary/50 hover:border-primary ring-1 ring-primary/20"
          : cardType === "trash"
            ? "border-destructive/20 opacity-75 hover:opacity-100 transition-opacity"
            : cardType === "shared"
              ? "hover:border-secondary/50 border-secondary/20"
              : "hover:border-primary/50",
        className
      )}
      onClick={onClick}
    >
      {/* Confidential stripe */}
      {confidential && (
        <div className="h-1.5 bg-red-600 shrink-0" />
      )}

      {/* Accent bar (no banner) */}
      {accentBar && !confidential && !bannerUrl && (
        <div className="h-1 bg-gradient-to-r from-primary via-primary/80 to-primary animate-pulse shrink-0" />
      )}

      {/* Banner */}
      {bannerUrl ? (
        <div
          data-slot="banner"
          className={cn("relative h-32 sm:h-48 w-full overflow-hidden shrink-0", grayscale && "grayscale")}
        >
          <img src={bannerUrl} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/30 to-transparent" />

          {accentBar && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-primary animate-pulse" />
          )}

          {/* Badges on banner */}
          {badges && (
            <div data-slot="badges" className="absolute top-2 right-2 flex items-center gap-1">
              {badges}
            </div>
          )}
          {badgesLeft && (
            <div className="absolute top-2 left-2">{badgesLeft}</div>
          )}

          {/* Actions on banner — always shown, positioned below badges if both exist */}
          {actions && (
            <div
              data-slot="actions"
              className={cn("absolute right-2", badges ? "top-10" : "top-2")}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "pointer" }}
            >
              {actions}
            </div>
          )}
        </div>
      ) : (
        <div
          data-slot="banner"
          className={cn("relative h-32 sm:h-48 w-full overflow-hidden shrink-0 bg-muted/30 flex items-center justify-center", grayscale && "grayscale")}
        >
          <img src={logoSvg} alt="" className="w-16 h-16 opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />

          {accentBar && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-primary animate-pulse" />
          )}

          {badges && (
            <div data-slot="badges" className="absolute top-2 right-2 flex items-center gap-1">
              {badges}
            </div>
          )}
          {badgesLeft && (
            <div className="absolute top-2 left-2">{badgesLeft}</div>
          )}
          {actions && (
            <div
              data-slot="actions"
              className={cn("absolute right-2", badges ? "top-10" : "top-2")}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: "pointer" }}
            >
              {actions}
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-2 flex flex-col flex-1 min-h-0 pt-2">
        {/* Title row */}
        <div data-slot="title-row" className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            {isImageIcon ? (
              <img
                data-slot="icon"
                src={icon}
                alt=""
                className={cn("w-10 h-10 rounded-lg object-cover shrink-0", grayscale && "grayscale")}
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            ) : (
              <span data-slot="icon" className="text-3xl shrink-0">{displayIcon}</span>
            )}
            <h3
              data-slot="name"
              className="font-semibold break-words min-w-0 line-clamp-2"
              style={{
                fontSize: title.length > 16
                  ? `${Math.max(20, 24 - (title.length - 16) * 0.5)}px`
                  : undefined,
                lineHeight: title.length > 16 ? '1.2' : undefined,
              }}
            >
              {title}
            </h3>
          </div>
        </div>

        {/* Extra content (e.g. world+character row for adventures) */}
        {extraContent}

        {/* Description */}
        {description ? (
          <p data-slot="description" className="text-sm text-muted-foreground line-clamp-2 sm:line-clamp-4 flex-1">
            {description}
          </p>
        ) : (
          <div className="flex-1" />
        )}

        {/* Footer */}
        {(footer || footerActions) && (
          <div data-slot="footer" className="flex items-center justify-between mt-auto">
            <div>{footer}</div>
            {footerActions && (
              <div
                data-slot="actions"
                className=""
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: "pointer" }}
              >
                {footerActions}
              </div>
            )}
          </div>
        )}

        {/* Bottom actions (trash restore/delete) */}
        {bottomActions && (
          <div data-slot="bottom-actions" className="mt-auto pt-1">
            {bottomActions}
          </div>
        )}
      </div>
    </Card>
  );
}
